import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Star, Quote, ChevronDown, ChevronUp } from 'lucide-react';

const GOOGLE_REVIEWS_URL = 'https://g.page/r/CeKCieX6qmc0EBM/review';

// Real Google reviews of Net Gains Real Estate (verified from Google Business Profile)
const reviews = [
  {
    id: 1,
    name: 'Dr. Rajesh Bariker',
    badge: '4 reviews',
    initial: 'R',
    color: 'from-purple-500 to-purple-600',
    rating: 5,
    date: '4 months ago',
    text: "Deepa has been an absolute pillar of support from day one of our journey in Dubai. She went above and beyond at every step, guiding us with clarity, patience, and an exceptional eye for detail. What truly sets her apart is her rare ability to combine deep market expertise with genuine care for her clients."
  },
  {
    id: 2,
    name: 'Malika Aluthge',
    badge: 'Local Guide · 17 reviews',
    initial: 'M',
    color: 'from-blue-500 to-blue-600',
    rating: 5,
    date: '8 months ago',
    text: "I've had the pleasure of working with Deepa in Dubai's commercial real estate market for the past 3+ years, and she's absolutely amazing at what she does! Her professionalism, honesty, and in-depth knowledge of the market make every deal seamless."
  },
  {
    id: 3,
    name: 'Ritesh Shroff',
    badge: '4 reviews',
    initial: 'R',
    color: 'from-emerald-500 to-emerald-600',
    rating: 5,
    date: '4 months ago',
    text: "My association with Ms Deepa of Net Gains has stretched across the last 4 years. The one thing which stands out is her focus on customer happiness. Never once has she ever pushed us into any deal but rather continued to explore further options until we found the right fit."
  },
  {
    id: 4,
    name: 'Sajeena Arvind',
    badge: '9 reviews',
    initial: 'S',
    color: 'from-amber-500 to-amber-600',
    rating: 5,
    date: '6 months ago',
    text: "Buying an apartment is a big decision, and I'm truly grateful to Deepa and Net Gains Real Estate for making the journey so smooth. Deepa was patient, honest, and always available to guide me at every step. Her genuine care and expertise made all the difference."
  },
  {
    id: 5,
    name: 'SPECTA GULF PACKAGING LLC',
    badge: 'Business Client',
    initial: 'S',
    color: 'from-red-500 to-red-600',
    rating: 5,
    date: '5 months ago',
    text: "Ms. Deepaa Tejnani is a highly professional and experienced real estate agent. She demonstrates strong knowledge of the real estate process and provides clear, accurate information to both buyers and sellers. She is dependable, responsive, and truly client-focused."
  },
  {
    id: 6,
    name: 'Sukrit Arya',
    badge: '3 reviews',
    initial: 'S',
    color: 'from-indigo-500 to-indigo-600',
    rating: 5,
    date: '4 months ago',
    text: "Great support at every step. Ms Deepaa is knowledgeable and is very well connected with various authorities. She understands the requirements before taking action. She supports until the tasks are complete."
  },
  {
    id: 7,
    name: 'Muhalab Adam',
    badge: 'Local Guide · 5 reviews',
    initial: 'M',
    color: 'from-teal-500 to-teal-600',
    rating: 5,
    date: '9 months ago',
    text: "Deepaa is the best ✌🏽😎"
  },
  {
    id: 8,
    name: 'Royal Arab',
    badge: '1 review',
    initial: 'R',
    color: 'from-rose-500 to-rose-600',
    rating: 5,
    date: '2 months ago',
    text: "We recently acquired a labor camp building in Dubai through Deepaa Tejnani at Net Gains Real Estate, and the experience was outstanding. She handled the entire transaction smoothly from start to finish. Her knowledge of labor accommodation and industrial properties in Dubai is exceptional, and her network helped move things forward quickly and efficiently. What really stood out was her availability — Deepaa was responsive at all times, even beyond regular working hours. That level of commitment is rare in the Dubai real estate market. Even after closing, she has continued to support us with queries related to the property, which shows her long-term approach and professionalism. If you're looking for a reliable commercial real estate broker in Dubai, especially for labor camps or other commercial assets, we highly recommend Deepaa. A well-deserved 5-star experience."
  },
  {
    id: 9,
    name: 'Sheetal Mihir',
    badge: 'Local Guide · 20 reviews',
    initial: 'S',
    color: 'from-fuchsia-500 to-fuchsia-600',
    rating: 5,
    date: '8 months ago',
    text: "As a GeoVastu Expert, I truly appreciate NetGains Real Estate for their professional approach and deep understanding of client needs. Their support has helped me offer my clients properties that are not only aesthetically appealing but also Vastu-compliant and energetically balanced."
  }
];

const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 180;
  const displayText = expanded || !isLong ? review.text : `${review.text.slice(0, 180)}...`;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 relative flex flex-col h-full" data-testid={`review-card-${review.id}`}>
      <Quote className="absolute top-4 right-4 w-10 h-10 text-[#C5A038] opacity-15" />

      {/* Header - avatar + name */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${review.color} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
          {review.initial}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-bold text-gray-900 text-sm truncate">{review.name}</h4>
          <p className="text-xs text-gray-500 truncate">{review.badge}</p>
        </div>
      </div>

      {/* Stars + date */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          ))}
        </div>
        <span className="text-xs text-gray-400">{review.date}</span>
      </div>

      {/* Review text */}
      <p className="text-gray-700 text-sm leading-relaxed mb-4 flex-1">
        "{displayText}"
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
        {isLong ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-[#12532c] font-semibold hover:text-[#0d3f22] flex items-center gap-1"
            data-testid={`review-toggle-${review.id}`}
          >
            {expanded ? (
              <>Show less <ChevronUp className="w-3 h-3" /></>
            ) : (
              <>Read more <ChevronDown className="w-3 h-3" /></>
            )}
          </button>
        ) : (
          <span />
        )}
        <img
          src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
          alt="Google"
          className="h-4 opacity-60"
        />
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Google Rating Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg border border-gray-100">
            <img
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
              alt="Google"
              className="h-6 mr-3"
            />
            <div className="flex items-center">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
              <span className="ml-2 text-lg font-bold text-gray-900">5.0</span>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t({ en: 'What Our Clients Say', ar: 'ماذا يقول عملاؤنا' })}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t({
              en: 'Verified reviews from investors and business owners we\'ve had the privilege to serve.',
              ar: 'مراجعات موثّقة من مستثمرين ورجال أعمال حظينا بشرف خدمتهم.'
            })}
          </p>
        </div>

        {/* Reviews Grid — Real Google Reviews */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="reviews-grid">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>

        {/* Trust Indicators - Only 3 verified stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-2xl p-6 text-center shadow-lg" data-testid="stat-google-rating">
            <div className="flex justify-center mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <div className="text-4xl font-bold text-[#12532c] mb-1">5.0</div>
            <div className="text-sm font-semibold text-gray-700">
              {t({ en: 'Google Rating', ar: 'تقييم Google' })}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 text-center shadow-lg" data-testid="stat-deals-closed">
            <div className="text-4xl font-bold text-[#12532c] mb-1 mt-6">100+</div>
            <div className="text-sm font-semibold text-gray-700">
              {t({ en: 'Property Deals Closed', ar: 'صفقة عقارية مُنجزة' })}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 text-center shadow-lg" data-testid="stat-satisfaction">
            <div className="text-4xl font-bold text-[#12532c] mb-1 mt-6">100%</div>
            <div className="text-sm font-semibold text-gray-700">
              {t({ en: 'Client Satisfaction', ar: 'رضا العملاء' })}
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-[#12532c] hover:bg-[#0d3f22] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
            data-testid="google-reviews-link"
          >
            <img
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
              alt=""
              className="h-5 mr-3 brightness-0 invert"
            />
            <span>{t({ en: 'Leave Us a Review on Google', ar: 'اترك لنا مراجعة على Google' })}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
